import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Check, GraduationCap, Sparkle, Buildings, User } from "@phosphor-icons/react";

const PLAN_ICONS = {
  "Piano Formatore": User,
  "Piano Struttura": Buildings,
};

const INTERVAL_LABEL = {
  week: "settimana",
  month: "mese",
  year: "anno",
};

const INTERVAL_BADGE = {
  week: { label: "Settimanale", tone: "bg-emerald-100 text-emerald-800" },
  month: { label: "Mensile",    tone: "bg-slate-100 text-slate-700" },
  year:  { label: "Annuale",    tone: "bg-amber-100 text-amber-800" },
};

export default function BillingPage() {
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([
          api.get("/billing/config"),
          api.get("/billing/me"),
        ]);
        setConfig(c.data);
        setMe(m.data);
      } catch (e) {
        toast.error("Impossibile caricare i piani");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel checkout");
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/billing`,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel portale");
      setBusy("");
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento…</div>;

  const plans = config?.plans || [];

  return (
    <div>
      <div className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-center gap-2 font-heading text-xl font-semibold">
          <CreditCard size={22} weight="fill" /> Abbonamento didattico
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Accesso al modulo formativo @Tiger Professional. Nessun uso clinico previsto.
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-6xl mx-auto">
        {me?.has_subscription && (
          <div data-testid="current-subscription" className="bg-white border border-emerald-300 bg-emerald-50 rounded-md p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-800">Abbonamento attivo</div>
                <div className="font-heading text-lg font-semibold mt-1">
                  {me.plan_name} · {me.interval_label}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {me.cancel_at_period_end
                    ? `Termina il ${me.current_period_end?.slice(0, 10)}`
                    : `Rinnovo il ${me.current_period_end?.slice(0, 10) || "-"}`}
                </div>
              </div>
              <Button
                data-testid="open-portal"
                onClick={openPortal}
                disabled={busy === "portal"}
                variant="outline"
                className="min-h-[44px]"
              >
                {busy === "portal" ? "Apertura…" : "Gestisci abbonamento"}
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.name] || Sparkle;
            const isFeatured = p.lookup_key === "tiger_formatore_weekly";
            const badge = INTERVAL_BADGE[p.interval] || INTERVAL_BADGE.month;
            return (
              <div
                key={p.lookup_key}
                data-testid={`plan-${p.lookup_key}`}
                className={`bg-white rounded-md p-5 border flex flex-col ${
                  isFeatured ? "border-slate-900 ring-1 ring-slate-900 shadow-lg" : "border-slate-200"
                }`}
              >
                {isFeatured && (
                  <div className="text-[10px] font-bold uppercase tracking-widest text-white bg-slate-900 rounded-full self-start px-2 py-0.5 mb-2">
                    Piu popolare
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                    isFeatured ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
                  }`}>
                    <Icon size={20} weight="fill" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading text-base font-semibold truncate">{p.name}</div>
                    <span className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full mt-0.5 ${badge.tone}`}>
                      {badge.label}
                    </span>
                  </div>
                </div>

                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-mono text-3xl font-bold">€{p.amount_eur.toFixed(2).replace('.', ',')}</span>
                  <span className="text-xs text-slate-500 ml-1">
                    / {INTERVAL_LABEL[p.interval] || p.interval}
                  </span>
                </div>
                <div className="text-[10px] text-slate-400 mb-4">IVA esclusa · IVA italiana calcolata da Stripe al checkout</div>

                <ul className="space-y-1.5 mb-5 flex-1">
                  {p.features.map((f, i) => (
                    <li key={`${p.lookup_key}-f-${i}`} className="flex items-start gap-2 text-xs text-slate-700">
                      <Check size={14} weight="bold" className="text-emerald-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => checkout(p.lookup_key)}
                  disabled={busy === p.lookup_key}
                  data-testid={`checkout-${p.lookup_key}`}
                  className={`w-full min-h-[40px] text-sm ${
                    isFeatured ? "bg-slate-900 hover:bg-slate-800" : ""
                  }`}
                  variant={isFeatured ? "default" : "outline"}
                >
                  {busy === p.lookup_key ? "Reindirizzamento…" : "Attiva ora"}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="rounded-md bg-amber-50 border border-amber-300 p-4 flex items-start gap-3">
          <GraduationCap size={20} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            <div className="font-semibold uppercase tracking-widest">Modulo formativo</div>
            <div className="mt-1">
              L'abbonamento da accesso ad uno strumento didattico e informativo. @Tiger Professional non e un
              dispositivo medico ai sensi del Regolamento UE 2017/745 (MDR) e non e destinato all'uso clinico
              reale in Pronto Soccorso. Pagamenti gestiti da Stripe. Puoi annullare in qualsiasi momento dal
              portale di gestione.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
